import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CONTACT_API_URL as API_URL } from './lib/api-config';
import { useAuth } from './contexts/AuthContext';
import LoadingSpinner from './components/ui/LoadingSpinner';
import AlertMessage from './components/ui/AlertMessage';

const api = axios.create({ withCredentials: true });

function Campaigns() {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterType, setFilterType] = useState('');
  const [tags, setTags] = useState([]); // eslint-disable-line no-unused-vars
  const [contacts, setContacts] = useState([]);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'newsletter',
    subject: '',
    htmlContent: '',
    targetSegment: { marketingConsent: true },
    triggerType: '',
    triggerValue: '',
    scheduledAt: ''
  });

  // Step form
  const [showStepForm, setShowStepForm] = useState(false);
  const [stepData, setStepData] = useState({
    name: '',
    subject: '',
    htmlContent: '',
    delayHours: 0
  });

  useEffect(() => {
    fetchCampaigns();
    fetchTags();
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType]);

  const fetchCampaigns = async () => {
    try {
      const url = filterType
        ? `${API_URL}/campaigns?type=${filterType}`
        : `${API_URL}/campaigns`;
      const response = await api.get(url);
      setCampaigns(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setError('Kunde inte hämta kampanjer');
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await api.get(`${API_URL}/tags`);
      setTags(response.data.data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await api.get(`${API_URL}/contacts`);
      setContacts(response.data.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSegmentChange = (key, value) => {
    setFormData({
      ...formData,
      targetSegment: { ...formData.targetSegment, [key]: value }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.type) {
      setError('Namn och typ krävs');
      return;
    }

    const payload = {
      ...formData,
      triggerType: formData.triggerType || null,
      triggerValue: formData.triggerValue || null,
      scheduledAt: formData.scheduledAt || null,
    };

    try {
      if (editingItem) {
        await api.put(`${API_URL}/campaigns/${editingItem.id}`, payload);
        setSuccess('Kampanj uppdaterad');
      } else {
        await api.post(`${API_URL}/campaigns`, payload);
        setSuccess('Kampanj skapad');
      }

      resetForm();
      fetchCampaigns();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunde inte spara kampanj');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'newsletter',
      subject: '',
      htmlContent: '',
      targetSegment: { marketingConsent: true },
      triggerType: '',
      triggerValue: '',
      scheduledAt: ''
    });
    setShowForm(false);
    setEditingItem(null);
  };

  const handleEdit = (campaign) => {
    setFormData({
      name: campaign.name || '',
      description: campaign.description || '',
      type: campaign.type || 'newsletter',
      subject: campaign.subject || '',
      htmlContent: campaign.htmlContent || '',
      targetSegment: campaign.targetSegment || { marketingConsent: true },
      triggerType: campaign.triggerType || '',
      triggerValue: campaign.triggerValue || '',
      scheduledAt: campaign.scheduledAt ? campaign.scheduledAt.split('T')[0] : ''
    });
    setEditingItem(campaign);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Är du säker?')) return;

    try {
      await api.delete(`${API_URL}/campaigns/${id}`);
      setSuccess('Kampanj borttagen');
      fetchCampaigns();
      setSelectedCampaign(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunde inte ta bort kampanj');
    }
  };

  const handleSendCampaign = async (campaignId) => {
    if (!window.confirm('Skicka kampanjen nu?')) return;

    try {
      const response = await api.post(`${API_URL}/campaigns/${campaignId}/send`);
      setSuccess(response.data.message);
      fetchCampaigns();
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunde inte skicka kampanj');
    }
  };

  const handleTestSend = async (campaignId) => {
    const email = window.prompt('Skicka testmejl till:', user?.email || '');
    if (!email) return;
    setError('');
    try {
      const response = await api.post(`${API_URL}/campaigns/${campaignId}/test-send`, { email });
      setSuccess(response.data.message);
      setTimeout(() => setSuccess(''), 4000);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunde inte skicka testmejl');
    }
  };

  // Step management
  const handleAddStep = async (e) => {
    e.preventDefault();
    if (!selectedCampaign || !stepData.subject || !stepData.htmlContent) {
      setError('Ämne och innehåll krävs');
      return;
    }

    try {
      await api.post(`${API_URL}/campaigns/${selectedCampaign.id}/steps`, stepData);
      setSuccess('Steg tillagt');
      setShowStepForm(false);
      setStepData({ name: '', subject: '', htmlContent: '', delayHours: 0 });

      // Refresh campaign
      const response = await api.get(`${API_URL}/campaigns/${selectedCampaign.id}`);
      setSelectedCampaign(response.data.data);
      fetchCampaigns();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunde inte lägga till steg');
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Ta bort detta steg?')) return;

    try {
      await api.delete(`${API_URL}/campaigns/${selectedCampaign.id}/steps/${stepId}`);
      const response = await api.get(`${API_URL}/campaigns/${selectedCampaign.id}`);
      setSelectedCampaign(response.data.data);
      setSuccess('Steg borttaget');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Kunde inte ta bort steg');
    }
  };

  // Enrollment
  const handleEnrollContacts = async () => {
    if (selectedContacts.length === 0) {
      setError('Välj minst en kontakt');
      return;
    }

    try {
      const response = await api.post(`${API_URL}/campaigns/${selectedCampaign.id}/enroll`, {
        contactIds: selectedContacts
      });
      setSuccess(response.data.message);
      setShowEnrollModal(false);
      setSelectedContacts([]);

      // Refresh campaign
      const campResponse = await api.get(`${API_URL}/campaigns/${selectedCampaign.id}`);
      setSelectedCampaign(campResponse.data.data);
      fetchCampaigns();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.message || 'Kunde inte enrolla kontakter');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: '#6B7280',
      scheduled: '#F59E0B',
      active: '#10B981',
      paused: '#EF4444',
      completed: '#3B82F6'
    };
    const labels = {
      draft: 'Utkast',
      scheduled: 'Schemalagd',
      active: 'Aktiv',
      paused: 'Pausad',
      completed: 'Avslutad'
    };
    return (
      <span className="status-badge" style={{ backgroundColor: colors[status] }}>
        {labels[status] || status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    return type === 'newsletter' ? 'Nyhetsbrev' : 'Drip-kampanj';
  };

  return (
    <main className="App-main">
      <AlertMessage error={error} success={success} />

      <div className="toolbar">
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Stäng' : '+ Ny kampanj'}
        </button>

        <select
          className="filter-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="">Alla typer</option>
          <option value="newsletter">Nyhetsbrev</option>
          <option value="drip">Drip-kampanjer</option>
        </select>

        <div className="stats">
          Totalt: {campaigns.length} kampanjer |
          Aktiva: {campaigns.filter(c => c.status === 'active').length}
        </div>
      </div>

      {/* Campaign Form */}
      {showForm && (
        <div className="form-container campaign-form">
          <h2>{editingItem ? 'Redigera kampanj' : 'Skapa ny kampanj'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h3>Grundinformation</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Namn *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="T.ex. Nyhetsbrev januari"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Typ *</label>
                  <select name="type" value={formData.type} onChange={handleInputChange}>
                    <option value="newsletter">Nyhetsbrev (engångsutskick)</option>
                    <option value="drip">Drip-kampanj (automatisk sekvens)</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Beskrivning</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="2"
                />
              </div>
            </div>

            {formData.type === 'newsletter' && (
              <div className="form-section">
                <h3>E-postinnehåll</h3>
                <div className="form-group">
                  <label>Ämnesrad *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    placeholder="Ämne för mejlet"
                  />
                </div>
                <div className="form-group">
                  <label>E-postinnehåll *</label>
                  <textarea
                    name="htmlContent"
                    value={formData.htmlContent}
                    onChange={handleInputChange}
                    rows="10"
                    placeholder="Skriv eller klistra in text. Tomrad = ny paragraf."
                  />
                  <small>
                    Klistra in vanlig text med radbrytningar — systemet formaterar automatiskt.
                    Använd {'{{unsubscribe_url}}'} där avprenumerationslänken ska in.
                  </small>
                </div>
              </div>
            )}

            {formData.type === 'drip' && (
              <div className="form-section">
                <h3>Trigger</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Triggertyp</label>
                    <select name="triggerType" value={formData.triggerType} onChange={handleInputChange}>
                      <option value="">Manuell enrollment</option>
                      <option value="lead_magnet">Lead Magnet-nedladdning</option>
                      <option value="tag_added">Tagg tillagd</option>
                      <option value="course_booking">Kursbokning</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Triggervärde</label>
                    <input
                      type="text"
                      name="triggerValue"
                      value={formData.triggerValue}
                      onChange={handleInputChange}
                      placeholder="t.ex. lead-magnet-slug eller taggnamn"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="form-section">
              <h3>Målgrupp/Segment</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.targetSegment.marketingConsent === true}
                      onChange={(e) => handleSegmentChange('marketingConsent', e.target.checked ? true : undefined)}
                    />
                    Endast med marketing-samtycke
                  </label>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.targetSegment.status || ''}
                    onChange={(e) => handleSegmentChange('status', e.target.value || undefined)}
                  >
                    <option value="">Alla</option>
                    <option value="Lead">Leads</option>
                    <option value="Customer">Kunder</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Taggar (kommaseparerade)</label>
                <input
                  type="text"
                  value={formData.targetSegment.tags?.join(', ') || ''}
                  onChange={(e) => handleSegmentChange('tags', e.target.value ? e.target.value.split(',').map(t => t.trim()) : undefined)}
                  placeholder="AI-intresserad, Stockholm"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-success">
                {editingItem ? 'Uppdatera' : 'Skapa'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetForm}>
                Avbryt
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Campaign List */}
      {loading ? (
        <LoadingSpinner message="Laddar kampanjer..." />
      ) : (
        <div className="campaigns-grid">
          {campaigns.length === 0 ? (
            <div className="no-items">
              <p>Inga kampanjer ännu</p>
              <p>Klicka "+ Ny kampanj" för att skapa din första.</p>
            </div>
          ) : (
            campaigns.map(campaign => (
              <div
                key={campaign.id}
                className={`campaign-card ${selectedCampaign?.id === campaign.id ? 'selected' : ''}`}
                onClick={() => setSelectedCampaign(selectedCampaign?.id === campaign.id ? null : campaign)}
              >
                <div className="campaign-header">
                  <span className="campaign-type">{getTypeBadge(campaign.type)}</span>
                  {getStatusBadge(campaign.status)}
                </div>

                <h3>{campaign.name}</h3>
                {campaign.description && <p className="description">{campaign.description}</p>}

                <div className="campaign-stats">
                  <div className="stat">
                    <span className="stat-value">
                      {campaign.status === 'draft'
                        ? (campaign.estimatedRecipients ?? '–')
                        : (campaign.totalRecipients || 0)}
                    </span>
                    <span className="stat-label">
                      {campaign.status === 'draft' ? 'Uppskattad målgrupp' : 'Mottagare'}
                    </span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{campaign.totalSent || 0}</span>
                    <span className="stat-label">Skickade</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{campaign.totalOpened || 0}</span>
                    <span className="stat-label">Öppnade</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{campaign.totalClicked || 0}</span>
                    <span className="stat-label">Klick</span>
                  </div>
                </div>

                {campaign.steps?.length > 0 && (
                  <div className="steps-preview">
                    {campaign.steps.length} steg
                  </div>
                )}

                {/* Expanded details */}
                {selectedCampaign?.id === campaign.id && (
                  <div className="campaign-details" onClick={(e) => e.stopPropagation()}>
                    {/* Steps for drip campaigns */}
                    {campaign.type === 'drip' && (
                      <div className="detail-section">
                        <h4>Steg i kampanjen</h4>
                        {selectedCampaign.steps?.length > 0 ? (
                          <div className="steps-list">
                            {selectedCampaign.steps
                              .sort((a, b) => a.stepOrder - b.stepOrder)
                              .map((step, index) => (
                                <div key={step.id} className="step-item">
                                  <span className="step-number">{index + 1}</span>
                                  <div className="step-info">
                                    <strong>{step.name || step.subject}</strong>
                                    <small>
                                      {step.delayHours > 0 ? `Efter ${step.delayHours}h` : 'Omedelbart'} |
                                      Skickade: {step.totalSent || 0}
                                    </small>
                                  </div>
                                  <button
                                    className="btn btn-danger btn-small"
                                    onClick={() => handleDeleteStep(step.id)}
                                  >
                                    &times;
                                  </button>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="no-steps">Inga steg ännu</p>
                        )}

                        <button
                          className="btn btn-secondary btn-small"
                          onClick={() => setShowStepForm(!showStepForm)}
                        >
                          + Lägg till steg
                        </button>

                        {showStepForm && (
                          <form onSubmit={handleAddStep} className="step-form">
                            <input
                              type="text"
                              placeholder="Stegnamn"
                              value={stepData.name}
                              onChange={(e) => setStepData({...stepData, name: e.target.value})}
                            />
                            <input
                              type="text"
                              placeholder="Ämnesrad *"
                              value={stepData.subject}
                              onChange={(e) => setStepData({...stepData, subject: e.target.value})}
                              required
                            />
                            <input
                              type="number"
                              placeholder="Fördröjning (timmar)"
                              value={stepData.delayHours}
                              onChange={(e) => setStepData({...stepData, delayHours: parseInt(e.target.value) || 0})}
                              min="0"
                            />
                            <textarea
                              placeholder="HTML-innehåll *"
                              value={stepData.htmlContent}
                              onChange={(e) => setStepData({...stepData, htmlContent: e.target.value})}
                              rows="5"
                              required
                            />
                            <div className="step-form-actions">
                              <button type="submit" className="btn btn-success btn-small">Spara steg</button>
                              <button type="button" className="btn btn-secondary btn-small" onClick={() => setShowStepForm(false)}>Avbryt</button>
                            </div>
                          </form>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="campaign-actions">
                      {campaign.type === 'newsletter' && campaign.status === 'draft' && (
                        <>
                          <button
                            className="btn btn-secondary"
                            onClick={() => handleTestSend(campaign.id)}
                          >
                            Skicka testmejl
                          </button>
                          <button
                            className="btn btn-success"
                            onClick={() => handleSendCampaign(campaign.id)}
                          >
                            Skicka nu
                          </button>
                        </>
                      )}
                      {campaign.type === 'drip' && (
                        <button
                          className="btn btn-success"
                          onClick={() => setShowEnrollModal(true)}
                        >
                          Enrolla kontakter
                        </button>
                      )}
                      <button className="btn btn-secondary" onClick={() => handleEdit(campaign)}>
                        Redigera
                      </button>
                      <button className="btn btn-danger" onClick={() => handleDelete(campaign.id)}>
                        Ta bort
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Enroll Modal */}
      {showEnrollModal && selectedCampaign && (
        <div className="modal-overlay" onClick={() => setShowEnrollModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Enrolla kontakter i {selectedCampaign.name}</h2>
            <div className="contact-select-list">
              {contacts.map(contact => (
                <label key={contact.id} className="contact-select-item">
                  <input
                    type="checkbox"
                    checked={selectedContacts.includes(contact.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedContacts([...selectedContacts, contact.id]);
                      } else {
                        setSelectedContacts(selectedContacts.filter(id => id !== contact.id));
                      }
                    }}
                  />
                  <span>{contact.firstName} {contact.lastName}</span>
                  <span className="contact-email">{contact.email}</span>
                </label>
              ))}
            </div>
            <div className="modal-actions">
              <button className="btn btn-success" onClick={handleEnrollContacts}>
                Enrolla {selectedContacts.length} kontakter
              </button>
              <button className="btn btn-secondary" onClick={() => setShowEnrollModal(false)}>
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Campaigns;
